/* global Blockly */
import React from 'react';
import PropTypes from 'prop-types';
import util from '../../../../../../utils/es5Utils';
import AsyncResponseValueStatementCore from '../../helpers/AsyncResponseValueStatementCore';
import { buttonImages } from '../../../../../../blocklyConstants';
import { fileuploadIcon } from '../../../v1/blocks/WidgetValues/FileUpload';

const WIDGET_SOURCE = 'widget';
const CUSTOM_SOURCE = 'custom';
const SOURCE_OPTIONS = [['File Upload widgets', WIDGET_SOURCE], ['local device files', CUSTOM_SOURCE]];
const SELECT_PLACEHOLDER = '- select -';
// Matches the warning written to the default warning slot by FieldWidgetsDropdown.init.
const MISSING_WIDGET_WARNING = 'This block refers to a Snap widget that does not exist, please modify or remove';

export const BLOCK_NAME = 'snap_create_zip_file_fileupload';

function defineBlock(context, totalWidgetCount) {
    let prop = {...context};
    prop.tooltipText = "Create a ZIP file from File Upload widgets or local device files.";
    prop.responseText = "result";
    prop.responseBlockType = "text_response";
    prop.blockColor = Blockly.Msg.APP_ACTIONS_HUE;
    prop.helpAnchor = '#file-uploads';
    let blockInstance = AsyncResponseValueStatementCore(prop);

    Object.assign(blockInstance, {
        filesCount: 0,
        source: totalWidgetCount > 0 ? WIDGET_SOURCE : CUSTOM_SOURCE,

        addFields: function () {
            this.appendValueInput('ZIP_FILE_NAME')
                .appendField("create ZIP file with name")
                .setCheck("String");

            this.appendDummyInput('SOURCE')
                .appendField("select files from")
                .appendField(new Blockly.FieldDropdown(
                    totalWidgetCount > 0 ? SOURCE_OPTIONS : [SOURCE_OPTIONS[1], SOURCE_OPTIONS[0]],
                    this.onSourceChanged_.bind(this)
                ), "SOURCE")
                .setOnNewRow(true);

            this.addFile_();

            this.appendValueInput('PARENT_FOLDER')
                .appendField("save in folder")
                .setCheck("String")
                .setOnNewRow(true);

            this.appendValueInput('CHILD_FOLDER')
                .appendField("subfolder")
                .setCheck("String")
                .setOnNewRow(true);

            this.appendValueInput('OVERWRITE')
                .appendField("overwrite")
                .setCheck("Boolean")
                .setOnNewRow(true);
        },

        // widgets already picked in other rows are excluded from a row's dropdown options; the callback keeps sibling rows in sync when this one changes
        createFileWidgetDropdown_: function (fileInputName) {
            let excludedIds = new Set(this.otherSelectedFileWidgetIds_(fileInputName));
            let widgetFilter = (w) => w.type === 'fileupload' && !excludedIds.has(w.id);
            // wired after construction, not via the constructor's opt_on_change - that fires onChange mid-construction (before the field is attached to any input) and corrupts the block's inputList
            let field = new Blockly.FieldWidgetsDropdown(context, widgetFilter, undefined, SELECT_PLACEHOLDER);
            field.setOnChange(this.refreshFileWidgetDropdowns_.bind(this, fileInputName));
            return field;
        },

        // ids currently selected in every widget-mode row other than the given one (ignores the unselected placeholder)
        otherSelectedFileWidgetIds_: function (excludeInputName) {
            let ids = [];
            for (let i = 1; i <= this.filesCount; i++) {
                let name = this.fileInputName_(i);
                if (name === excludeInputName) continue;
                let value = this.getFieldValue(name);
                if (value && value !== SELECT_PLACEHOLDER) {
                    ids.push(value);
                }
            }
            return ids;
        },

        // rebuilds every OTHER widget-mode row's dropdown so it excludes whatever is now picked in changedInputName (pass nothing to refresh all rows, e.g. after a row is removed)
        refreshFileWidgetDropdowns_: function (changedInputName) {
            if (!this.isWidgetMode_()) return;

            for (let i = 1; i <= this.filesCount; i++) {
                let fileInputName = this.fileInputName_(i);
                if (fileInputName === changedInputName) continue;

                let input = this.getInput(fileInputName);
                if (!input) continue;
                let currentValue = this.getFieldValue(fileInputName);
                let isLastRow = (i === this.filesCount);

                input.removeField(fileInputName);
                if (isLastRow) {
                    input.removeField('FILES_MINUS');
                    input.removeField('FILES_PLUS');
                }

                let newField = this.createFileWidgetDropdown_(fileInputName);
                input.appendField(newField, fileInputName);
                if (currentValue && currentValue !== SELECT_PLACEHOLDER) {
                    this.restoreFieldValue_(newField, currentValue);
                }

                if (isLastRow) {
                    this.appendButtons_(input);
                }
            }

            this.layoutFileRows_();

            if (this.rendered) {
                this.render();
            }
        },

        // sync both buttons after appending fields, which initially start visible
        updateButtonVisibility_: function () {
            // FILES_MINUS doesn't exist once the last row has been removed (filesCount === 0), between teardown and rebuild
            this.getField('FILES_MINUS')?.setVisible(this.filesCount > 1);
            let plusField = this.getField('FILES_PLUS');
            if (!plusField) return;
            let noWidgetsLeft = this.isWidgetMode_() && this.filesCount >= totalWidgetCount;
            let lastRowUnselected = this.isWidgetMode_() && this.getFieldValue(this.fileInputName_(this.filesCount)) === SELECT_PLACEHOLDER;
            plusField.setVisible(!noWidgetsLeft && !lastRowUnselected);
        },

        // re-asserts every file row's position right before PARENT_FOLDER, in order - guards against rows drifting after a rebuild
        repositionFileRows_: function () {
            if (!this.getInput('PARENT_FOLDER')) return;
            for (let i = 1; i <= this.filesCount; i++) {
                this.moveInputBefore(this.fileInputName_(i), 'PARENT_FOLDER');
                if (this.isCustomMode_()) {
                    this.moveInputBefore(this.buttonsHostInputName_(i), 'PARENT_FOLDER');
                }
            }
        },

        layoutFileRows_: function () {
            this.repositionFileRows_();
            this.updateButtonVisibility_();
        },

        // match the overwrite shadow to the action block; result and user-supplied Boolean blocks keep their native colours
        customChangeHandler: function () {
            let overwriteBlock = this.getInput('OVERWRITE')?.connection?.targetBlock();
            if (overwriteBlock?.isShadow() && overwriteBlock.customColor !== Blockly.Msg.APP_ACTIONS_HUE) {
                overwriteBlock.setColour(Blockly.Msg.APP_ACTIONS_HUE);
                overwriteBlock.customColor = Blockly.Msg.APP_ACTIONS_HUE;
            }
        },

        clearMissingWidgetWarning_: function () {
            // Removing a field doesn't clear its warning. Only clear the default slot if
            // it still belongs to the removed widget dropdown; retain other warning IDs.
            if (this.warning?.text_?.[''] !== MISSING_WIDGET_WARNING) return;
            if (Object.keys(this.warning.text_).length === 1) {
                this.setWarningText(null);
            } else {
                this.warning.setText('', '');
            }
        },

        // dropdown validator: rebuilds rows for the newly selected source, restoring that source's remembered values if any (else 1 blank row for widget, or the current row count for custom)
        onSourceChanged_: function (newSource) {
            if (newSource === this.source) return newSource;

            if (this.isWidgetMode_()) {
                this.rememberedWidgetSelections = this.otherSelectedFileWidgetIds_();
            } else {
                this.rememberedCustomBlocks = this.captureCustomFileBlocks_();
                this.rememberedCustomValues = undefined;
            }

            let fallbackRowCount = newSource === WIDGET_SOURCE ? 1 : this.filesCount;
            while (this.filesCount > 0) {
                this.removeFile_(true);
            }
            this.source = newSource;

            let customSeeds = this.rememberedCustomBlocks?.length ? this.rememberedCustomBlocks : this.rememberedCustomValues;
            let seeds = newSource === WIDGET_SOURCE ? this.rememberedWidgetSelections : customSeeds;
            let rowCount = seeds?.length > 0 ? seeds.length : fallbackRowCount;
            for (let i = 0; i < rowCount; i++) {
                this.addFile_(seeds?.[i], true);
            }
            this.layoutFileRows_();
            // seeded rows are set independently of each other, so sibling widget dropdowns still need their exclusion lists synced
            this.refreshFileWidgetDropdowns_();
            if (this.isCustomMode_()) {
                this.clearMissingWidgetWarning_();
            }

            if (this.rendered) {
                this.render();
            }

            return newSource;
        },

        // Snapshot whole value trees, not just text fields: a URI can be a Text Join, widget getter, etc.
        captureCustomFileBlocks_: function () {
            let blocks = [];
            for (let i = 1; i <= this.filesCount; i++) {
                let targetBlock = this.getInput(this.fileInputName_(i))?.connection?.targetBlock();
                blocks.push(targetBlock ? { xml: Blockly.Xml.domToText(Blockly.Xml.blockToDom(targetBlock, true)) } : null);
            }
            return blocks;
        },

        // Recreate an inactive custom input's saved expression with fresh IDs.
        restoreCustomFileXml_: function (fileInput, text) {
            let existingRoots = new Set(this.workspace.getTopBlocks?.(false) || []);
            let restoredBlock;
            try {
                let xml = Blockly.Xml.textToDom(text);
                if (!['block', 'shadow'].includes(xml.nodeName.toLowerCase())) {
                    throw new Error('Expected a Blockly block or shadow');
                }
                restoredBlock = Blockly.Xml.domToBlock(xml, this.workspace);
                fileInput.connection.connect(restoredBlock.outputConnection);
                return true;
            } catch (e) {
                // A damaged inactive-mode snapshot must not prevent the workspace from opening.
                restoredBlock?.dispose();
                for (let block of this.workspace.getTopBlocks?.(false) || []) {
                    if (!existingRoots.has(block)) block.dispose();
                }
                console.warn('CreateZipFile: ignoring corrupted custom file block', e);
                return false;
            }
        },

        // Old mutations used plain text seeds; a missing or damaged snapshot gets a default shadow.
        restoreCustomFileBlock_: function (fileInput, seed) {
            if (seed && typeof seed.xml === 'string' && this.restoreCustomFileXml_(fileInput, seed.xml)) return;

            let inputBlock = this.workspace.newBlock('text');
            inputBlock.setShadow(true);
            inputBlock.setMovable(false);
            if (typeof seed === 'string') {
                inputBlock.setFieldValue(seed, 'TEXT');
            }
            fileInput.connection.connect(inputBlock.outputConnection);
            if (this.rendered) {
                inputBlock.initSvg();
                inputBlock.render();
            }
        },

        // seed is a widget id, a legacy text path or a snapshot of a complete custom expression.
        // deferFinalize skips repeated reposition/render work while rebuilding multiple rows.
        addFile_: function (seed, deferFinalize) {
            // the +/- buttons always trail whichever row is currently last, so detach them before adding a new last row
            if (this.filesCount > 0) {
                let previousButtonsHost = this.getInput(this.buttonsHostInputName_(this.filesCount));
                previousButtonsHost.removeField('FILES_MINUS');
                previousButtonsHost.removeField('FILES_PLUS');
            }

            this.filesCount++;
            let fileInputName = this.fileInputName_(this.filesCount);
            let buttonsHost;

            if (this.isWidgetMode_()) {
                let field = this.createFileWidgetDropdown_(fileInputName);
                buttonsHost = this.appendDummyInput(fileInputName)
                    .appendField(new Blockly.FieldImage(fileuploadIcon, 24, 24, "fileupload icon", null), `${fileInputName}_ICON`)
                    .appendField(field, fileInputName)
                    .setOnNewRow(true);

                if (seed) {
                    this.restoreFieldValue_(field, seed);
                }
            } else {
                let fileInput = this.appendValueInput(fileInputName)
                    .setCheck("String")
                    .setOnNewRow(true);

                this.restoreCustomFileBlock_(fileInput, seed);

                // separate input right after (not on a new row) so the +/- buttons render after the connected text block
                buttonsHost = this.appendDummyInput(`${fileInputName}_BTN`);
            }

            this.appendButtons_(buttonsHost);

            if (deferFinalize) return;

            // re-assert row ordering; on the very first row (still inside addFields) PARENT_FOLDER doesn't exist yet, so this is a no-op
            this.layoutFileRows_();

            if (this.rendered) {
                this.initSvg();
                this.render();
            }
        },

        // An explicit row removal releases real expression blocks onto the canvas. Internal
        // rebuilds dispose them after snapshotting so switching sources cannot create duplicates.
        removeFile_: function (disposeConnectedBlocks = false) {
            if (this.isCustomMode_()) {
                this.removeInput(this.buttonsHostInputName_(this.filesCount));
                if (disposeConnectedBlocks) {
                    let connection = this.getInput(this.fileInputName_(this.filesCount))?.connection;
                    let targetBlock = connection?.targetBlock();
                    if (targetBlock) {
                        connection.setShadowDom(null);
                        targetBlock.dispose();
                    }
                }
            }
            // Blockly disposes shadows and unplugs user-connected blocks when removing an input.
            this.removeInput(this.fileInputName_(this.filesCount));
            this.filesCount--;

            if (this.filesCount > 0) {
                this.appendButtons_(this.getInput(this.buttonsHostInputName_(this.filesCount)));
                this.updateButtonVisibility_();
            }

            // the removed row's widget (if any) becomes available again for every other row
            this.refreshFileWidgetDropdowns_();
            this.repositionFileRows_();

            if (this.rendered) {
                this.render();
            }
        },

        // appends the +/- buttons onto the given input, returning it for chaining
        appendButtons_: function (host) {
            return host
                // FieldImage passes itself to the click handler; do not forward it as the teardown flag.
                .appendField(new Blockly.FieldImage(buttonImages.minusImage, 24, 24, "*", () => this.removeFile_()), "FILES_MINUS")
                // FieldImage's click handler is invoked with the field itself as an argument - don't forward that into addFile_'s seed param
                .appendField(new Blockly.FieldImage(buttonImages.plusImage, 24, 24, "*", () => this.addFile_()), "FILES_PLUS");
        },

        // the input that currently hosts the +/- buttons for row n (own row in widget mode, trailing companion row in custom mode)
        buttonsHostInputName_: function (n) {
            return this.isWidgetMode_() ? this.fileInputName_(n) : `${this.fileInputName_(n)}_BTN`;
        },

        // canonical input name for row n (e.g. FILE_3) - the single source of truth every other row-naming helper builds on
        fileInputName_: function (n) {
            return `FILE_${n}`;
        },

        // convenience accessors since this.source's two values are checked all over the file
        isWidgetMode_: function () {
            return this.source === WIDGET_SOURCE;
        },
        isCustomMode_: function () {
            return this.source === CUSTOM_SOURCE;
        },

        // sets a widget dropdown's value while bypassing FieldDropdown's onChange-triggering setValue override (avoids
        // re-entering refreshFileWidgetDropdowns_); this also skips onItemSelected, which is what normally marks a real
        // selection as hasBeenSet, so we set that flag here too - centralized so both call sites stay in sync
        restoreFieldValue_: function (field, value) {
            Blockly.Field.prototype.setValue.call(field, value);
            field.hasBeenSet = true;
        },

        parseRememberedRows_: function (xml, attribute) {
            let saved = xml.getAttribute(attribute);
            if (!saved) return undefined;
            try {
                let rows = JSON.parse(saved);
                return Array.isArray(rows) ? rows : [];
            } catch (e) {
                console.warn(`CreateZipFile: ignoring corrupted ${attribute} mutation attribute`, e);
                return [];
            }
        },

        mutationToDom: function () {
            let container = Blockly.utils.xml.createElement('mutation');
            container.setAttribute('filesCount', this.filesCount);
            container.setAttribute('source', this.source);
            // persist the OTHER (currently inactive) mode's remembered rows too, so a save/reload round trip doesn't wipe them
            if (this.rememberedWidgetSelections?.length) {
                container.setAttribute('rememberedWidgetSelections', JSON.stringify(this.rememberedWidgetSelections));
            }
            if (this.rememberedCustomBlocks?.length) {
                container.setAttribute('rememberedCustomBlocks', JSON.stringify(this.rememberedCustomBlocks));
            }
            if (this.rememberedCustomValues?.length) {
                container.setAttribute('rememberedCustomValues', JSON.stringify(this.rememberedCustomValues));
            }
            return container;
        },

        domToMutation: function (xml) {
            // missing/corrupted attribute still rebuilds with the minimum valid row count
            let filesCount = Math.max(1, Number.parseInt(xml.getAttribute('filesCount')) || 1);
            let wasWidgetMode = this.isWidgetMode_();

            while (this.filesCount > 0) {
                this.removeFile_(true);
            }

            let savedSource = xml.getAttribute('source');
            let source = savedSource === WIDGET_SOURCE || savedSource === CUSTOM_SOURCE ? savedSource : this.source;
            this.source = source;
            if (this.getField('SOURCE')) {
                this.getField('SOURCE').setValue(source);
            }

            this.rememberedWidgetSelections = this.parseRememberedRows_(xml, 'rememberedWidgetSelections');
            this.rememberedCustomValues = this.parseRememberedRows_(xml, 'rememberedCustomValues');
            this.rememberedCustomBlocks = this.parseRememberedRows_(xml, 'rememberedCustomBlocks');

            // builds empty rows only - Blockly's own field/value XML loading fills in each row's saved widget selection or file path afterward, by matching field/value elements to these inputs by name
            for (let i = 0; i < filesCount; i++) {
                this.addFile_(undefined, true);
            }
            this.layoutFileRows_();
            if (wasWidgetMode && this.isCustomMode_()) {
                this.clearMissingWidgetWarning_();
            }

            // this.rendered means the block already has an SVG on the workspace (not a call to render) - only then is there something to refresh
            if (this.rendered) {
                this.render();
            }
        }
    });

    return blockInstance;
}

function defineGenerators() {
    return {
        'JavaScript': function (block) {
            let valueOrDefault = (input, fallback = '""') =>
                Blockly.JavaScript.valueToCode(block, input, Blockly.JavaScript.ORDER_NONE) || fallback;
            let zipFileName = valueOrDefault('ZIP_FILE_NAME');
            let parentFolder = valueOrDefault('PARENT_FOLDER');
            let childFolder = valueOrDefault('CHILD_FOLDER');
            let overwrite = valueOrDefault('OVERWRITE', 'false');

            let files = [];
            for (let i = 1; i <= block.filesCount; i++) {
                if (block.isWidgetMode_()) {
                    files.push(JSON.stringify(block.getFieldValue(block.fileInputName_(i))));
                } else {
                    files.push(valueOrDefault(block.fileInputName_(i)));
                }
            }

            let coreResponseCode = block.getJavascriptCore();
            return `Snap.file.zipFile({ zipFileName: ${zipFileName}, source: ${JSON.stringify(block.source)}, files: [${files.join(', ')}], parentFolder: ${parentFolder}, childFolder: ${childFolder}, overwrite: ${overwrite} }, ${coreResponseCode});\n`;
        }
    }
}

const CreateZipFile = function (props) {
    const name = BLOCK_NAME;
    CreateZipFile.blockName = name;
    const context = props.context || {};
    const totalWidgets = context.pages ? util.widgetsOfType(context, 'fileupload').length : 0;

    Blockly.Blocks[name] = defineBlock({ ...context, pages: context.pages || [] }, totalWidgets);
    let generators = defineGenerators();
    for (let i in generators) {
        Blockly[i][name] = generators[i];
    }

    return (
        <block type={name}>
            <value name="ZIP_FILE_NAME">
                <shadow type="text"/>
            </value>
            <value name="PARENT_FOLDER">
                <shadow type="text"/>
            </value>
            <value name="CHILD_FOLDER">
                <shadow type="text"/>
            </value>
            <value name="OVERWRITE">
                <shadow type="logic_boolean">
                    <field name="BOOL">FALSE</field>
                </shadow>
            </value>
        </block>
    );
}

CreateZipFile.propTypes = {
    context: PropTypes.object
};

export default CreateZipFile;
